// Authorship signing for the /photo-watermark tool.
//
// What it does (real, not stub):
//   1. SHA-256 of the JPEG bytes via WebCrypto — image never leaves the browser.
//   2. POST { hash_b64, manifest_json } to /api/c2pa-sign — server signs with a
//      persistent ECDSA P-256 key in storage/app/c2pa/.
//   3. Receive { signature, public_key, signed_at }.
//   4. Build an XMP packet with the signature, the public key, the signed time,
//      the author name plus a few standard Dublin Core / Photoshop tags so
//      Lightroom / Photoshop / ExifTool / macOS Finder see Creator and ©.
//   5. Inject the XMP APP1 marker into the JPEG. Return the modified blob.
//
// What it does NOT do (yet, v1.2):
//   - Adobe Bridge "Content Credentials valid" badge — requires C2PA-conformant
//     JUMBF manifest + cert chain rooted in the CAI trust list. We layer that on
//     once we buy a CAI-listed cert (~$300/yr). Our XMP packet is forward-compatible.
//   - PNG / WebP embedding — JPEG only for now. The UI gates non-JPEG output
//     downgrading from "Content Credentials" mode silently.
//
// Strictly additive: any failure path returns the input blob unchanged so the
// export pipeline keeps moving. The caller never sees a thrown error.
(function () {
    'use strict';

    const SLUG_CTX = 'c2pa-signer';
    const aeCrit = (event, payload) => {
        try { window.__aeCritical && window.__aeCritical(event, Object.assign({ slug: SLUG_CTX }, payload || {})); } catch (e) { }
    };

    const SIGN_ENDPOINT = '/api/c2pa-sign';

    // Endpoint health is learned lazily on the first real sign() call. Previously
    // we ran an active POST probe with an empty payload; the resulting 422 was
    // harmless but lit up QA-tooling network watchers as a "failure". Skipping
    // the probe also saves one round-trip per page load.
    let endpointKnownDead = false;

    function getCsrfToken() {
        // Laravel emits the XSRF-TOKEN cookie (URL-encoded JSON-with-MAC). Reading
        // it lets us POST same-origin without using a meta tag.
        const m = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
        if (!m) return null;
        try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
    }

    // ensure() returns ready unless we already proved the endpoint is missing.
    // We don't network-probe at all — the actual sign() call is the source of truth.
    async function ensure() {
        return endpointKnownDead ? { ready: false, reason: 'endpoint_missing' } : { ready: true };
    }

    async function sign(blob, opts) {
        opts = opts || {};
        try {
            const state = await ensure();
            if (!state.ready) {
                aeCrit('c2pa_skip', { reason: state.reason || 'unknown' });
                return blob;
            }

            const mime = opts.mime || blob.type || 'image/jpeg';
            if (mime !== 'image/jpeg') {
                // PNG / WebP XMP embed lands in v1.2; for now just emit the JPEG
                // metadata path and skip silently for other formats.
                aeCrit('c2pa_skip', { reason: 'format_not_supported', mime });
                return blob;
            }

            const bytes = new Uint8Array(await blob.arrayBuffer());
            const hashBytes = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
            const hashB64 = bytesToBase64(hashBytes);
            const manifest = buildManifest(opts);
            const manifestJson = JSON.stringify(manifest);

            const resp = await fetch(SIGN_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-XSRF-TOKEN': getCsrfToken() || '',
                },
                body: JSON.stringify({ hash_b64: hashB64, manifest_json: manifestJson }),
                credentials: 'same-origin',
            });
            if (!resp.ok) {
                // 404 means the endpoint isn't deployed — remember so subsequent
                // exports in this session skip immediately without re-trying.
                if (resp.status === 404) endpointKnownDead = true;
                aeCrit('c2pa_sign_http', { status: resp.status });
                return blob;
            }
            const data = await resp.json();
            if (!data || !data.signature_b64) {
                aeCrit('c2pa_sign_empty', {});
                return blob;
            }

            const xmpPacket = buildXmpPacket({
                manifest,
                hash_b64: hashB64,
                signature_b64: data.signature_b64,
                verify_url: data.verify_url || '',
                signed_at: data.signed_at,
                algorithm: data.algorithm || 'HMAC-SHA256',
            });

            return new Blob([injectXmpIntoJpeg(bytes, xmpPacket)], { type: 'image/jpeg' });
        } catch (e) {
            aeCrit('c2pa_sign_threw', { err: String((e && e.message) || e).slice(0, 140) });
            return blob;
        }
    }

    function buildManifest(opts) {
        opts = opts || {};
        const author = (opts.author || '').trim();
        // Every field below is also written into the XMP packet so the verifier
        // can reconstruct this object byte-for-byte. Don't add a `title` here —
        // we deliberately drop the filename because users rename the photo
        // after download and the verifier would never recover the original
        // filename to rebuild the signed JSON. Keep this object deterministic.
        const m = {
            claim_generator: 'Timbrica/1.0 photo-watermark',
            format: opts.mime || 'image/jpeg',
            instance_id: 'urn:uuid:' + uuid(),
            actions: [{ action: 'created' }, { action: 'watermarked' }],
            ai_training: 'notAllowed',
        };
        if (author) {
            m.author = author;
        }
        return m;
    }

    function buildXmpPacket(claim) {
        // XMP is XML inside `<?xpacket?>` wrappers. We embed:
        //  - standard Dublin Core / Photoshop tags so Lightroom & Finder see Creator/©
        //  - a custom timbrica namespace with the cryptographic claim
        // Keep XML content compact — JPEG APP1 markers are capped at 65 KB.
        const esc = (s) => String(s).replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[c]);
        const author = (claim.manifest && claim.manifest.author) ? esc(claim.manifest.author) : '';
        const year = new Date((claim.signed_at || Math.floor(Date.now() / 1000)) * 1000).getUTCFullYear();
        const isoDate = new Date((claim.signed_at || Math.floor(Date.now() / 1000)) * 1000).toISOString();
        const copyrightLine = author ? `© ${year} ${author}` : `© ${year}`;

        const body = `<?xpacket begin="\uFEFF" id="W5M0MpCehiHzreSzNTczkc9d"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/" x:xmptk="Timbrica Photo Watermark 1.0">
  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
    <rdf:Description rdf:about=""
      xmlns:dc="http://purl.org/dc/elements/1.1/"
      xmlns:xmp="http://ns.adobe.com/xap/1.0/"
      xmlns:xmpRights="http://ns.adobe.com/xap/1.0/rights/"
      xmlns:photoshop="http://ns.adobe.com/photoshop/1.0/"
      xmlns:timbrica="https://timbrica.com/photo-watermark/v1#">
      ${author ? `<dc:creator><rdf:Seq><rdf:li>${author}</rdf:li></rdf:Seq></dc:creator>` : ''}
      <dc:rights><rdf:Alt><rdf:li xml:lang="x-default">${esc(copyrightLine)}</rdf:li></rdf:Alt></dc:rights>
      ${author ? `<photoshop:Credit>${author}</photoshop:Credit>` : ''}
      <xmpRights:Marked>True</xmpRights:Marked>
      <xmpRights:WebStatement>https://timbrica.com/photo-watermark</xmpRights:WebStatement>
      <xmp:CreatorTool>Timbrica Photo Watermark 1.0</xmp:CreatorTool>
      <xmp:CreateDate>${isoDate}</xmp:CreateDate>
      <timbrica:claimGenerator>${esc(claim.manifest.claim_generator)}</timbrica:claimGenerator>
      <timbrica:instanceId>${esc(claim.manifest.instance_id)}</timbrica:instanceId>
      <timbrica:aiTraining>${esc(claim.manifest.ai_training)}</timbrica:aiTraining>
      <timbrica:imageHashAlgorithm>SHA-256</timbrica:imageHashAlgorithm>
      <timbrica:imageHashB64>${esc(claim.hash_b64)}</timbrica:imageHashB64>
      <timbrica:signedAt>${claim.signed_at}</timbrica:signedAt>
      <timbrica:signatureAlgorithm>${esc(claim.algorithm)}</timbrica:signatureAlgorithm>
      <timbrica:signatureB64>${esc(claim.signature_b64)}</timbrica:signatureB64>
      <timbrica:verifyUrl>${esc(claim.verify_url)}</timbrica:verifyUrl>
    </rdf:Description>
  </rdf:RDF>
</x:xmpmeta>
<?xpacket end="r"?>`;
        return body;
    }

    // Inject XMP APP1 marker into a JPEG byte stream.
    // APP1 layout for XMP:
    //   FF E1 [size-2-bytes-big-endian] http://ns.adobe.com/xap/1.0/\0 <XMP-bytes>
    function injectXmpIntoJpeg(jpegBytes, xmpString) {
        if (jpegBytes[0] !== 0xFF || jpegBytes[1] !== 0xD8) return jpegBytes;
        const xmpNs = 'http://ns.adobe.com/xap/1.0/\0';
        const xmpNsBytes = new TextEncoder().encode(xmpNs);
        const xmpBytes = new TextEncoder().encode(xmpString);
        const payloadLen = xmpNsBytes.length + xmpBytes.length;
        // APP1 segment length includes the 2 length bytes themselves but
        // excludes the FFE1 marker. Hard cap at 65533 for the length field.
        const segLen = 2 + payloadLen;
        if (segLen > 0xFFFF) {
            // XMP too large for a single APP1; v1.2 will split into Extended XMP.
            aeCrit('c2pa_xmp_too_large', { bytes: segLen });
            return jpegBytes;
        }
        const seg = new Uint8Array(2 + segLen);
        seg[0] = 0xFF;
        seg[1] = 0xE1;
        seg[2] = (segLen >> 8) & 0xFF;
        seg[3] = segLen & 0xFF;
        seg.set(xmpNsBytes, 4);
        seg.set(xmpBytes, 4 + xmpNsBytes.length);

        // Splice the new APP1 segment right after SOI (FFD8), before any
        // existing APP markers — this is the location XMP readers look at first.
        const out = new Uint8Array(jpegBytes.length + seg.length);
        out[0] = 0xFF;
        out[1] = 0xD8;
        out.set(seg, 2);
        out.set(jpegBytes.subarray(2), 2 + seg.length);
        return out;
    }

    function bytesToBase64(bytes) {
        let s = '';
        for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
        return btoa(s);
    }

    function uuid() {
        if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
        const r = new Uint8Array(16);
        if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(r);
        else for (let i = 0; i < 16; i++) r[i] = Math.floor(Math.random() * 256);
        r[6] = (r[6] & 0x0f) | 0x40;
        r[8] = (r[8] & 0x3f) | 0x80;
        const h = Array.from(r).map((b) => b.toString(16).padStart(2, '0'));
        return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`;
    }

    window.C2PA = { ensure, sign, buildManifest };
})();
